import ApiClient from ".."

// In DTOs
import CoachGetListIn from "../../dtos/in/CoachGetListIn"
import CoachGetReviewsIn from "../../dtos/in/CoachGetReviewsIn"
import CoachPatchAcceptingClientsIn from "../../dtos/in/CoachPatchAcceptingClientsIn"
import CoachPatchHourlyRateIn from "../../dtos/in/CoachPatchHourlyRateIn"
import CoachPatchPayeeNameIn from "../../dtos/in/CoachPatchPayeeNameIn"
import CoachPatchIsExecutiveIn from "../../dtos/in/CoachPatchIsExecutiveIn"
import CoachPostAssignedIn from "../../dtos/in/CoachPostAssignedIn"

// Out DTOs
import CoachGetAssignedOut from "../../dtos/out/CoachGetAssignedOut"
import CoachGetLifecyclePageOut from "../../dtos/out/CoachGetLifecyclePageOut"
import CoachGetListOut from "../../dtos/out/CoachGetListOut"
import type CoachGetOut from "../../dtos/out/CoachGetOut"
import CoachGetReviewsOut from "../../dtos/out/CoachGetReviewsOut"
import CoachGetSelectionOut from "../../dtos/out/CoachGetSelectionOut"
import GenericOut from "../../dtos/out/GenericOut"
import MetabaseEmbedOut from "../../dtos/out/MetabaseEmbedOut"
import CoachGetAssignmentOut from "../../dtos/out/CoachGetAssignmentOut"

export class CoachEndpoints {
  constructor(private client: ApiClient) {}
  static readonly BASE_URL = "/coach"

  /**
   * Retrieves a coach's details.
   */
  async get(coachId: number) {
    return this.client.get<CoachGetOut>(CoachEndpoints.BASE_URL, `/${coachId}`)
  }

  /**
   * Retrieves the authenticated user's assigned coach.
   */
  async getAssigned() {
    return this.client.get<CoachGetAssignedOut>(CoachEndpoints.BASE_URL, "/assigned")
  }

  /**
   * Retrieves a coach's lifecycle page details.
   */
  async getLifecycle(slug: string) {
    return this.client.get<CoachGetLifecyclePageOut>(CoachEndpoints.BASE_URL, `/lifecycle/${slug}`)
  }

  /**
   * Retrieves a list of coaches with filters, ordering, and pagination.
   */
  async getList(payload: CoachGetListIn) {
    return this.client.get<CoachGetListOut>(CoachEndpoints.BASE_URL, "/", payload)
  }

  /**
   * Retrieves a list of all coaches available for display (eg: on paywall).
   */
  async getListForDisplay() {
    return this.client.get<CoachGetSelectionOut>(CoachEndpoints.BASE_URL, "/display")
  }

  /**
   * Retrieves a list of all coaches available for self-selection by a user.
   */
  async getListForSelection() {
    return this.client.get<CoachGetSelectionOut>(CoachEndpoints.BASE_URL, "/selection")
  }

  /**
   * Retrieves a list of all coaches available for assignment by admin.
   */
  async getListForAssignment() {
    return this.client.get<CoachGetAssignmentOut>(CoachEndpoints.BASE_URL, "/assignment")
  }

  /**
   * Retrieves the currently authenticated coach.
   */
  async getMe() {
    return this.client.get<CoachGetOut>(CoachEndpoints.BASE_URL, "/me")
  }

  /**
   * Retrieves coach reviews.
   */
  async getReviews(payload: CoachGetReviewsIn) {
    return this.client.get<CoachGetReviewsOut>(CoachEndpoints.BASE_URL, "/reviews", payload)
  }

  /**
   * Retrieves reviews for specific coach.
   */
  async getCoachReviews(coachId: number, payload: CoachGetReviewsIn) {
    return this.client.get<CoachGetReviewsOut>(CoachEndpoints.BASE_URL, `/${coachId}/reviews`, payload)
  }

  /**
   * Retrieves url for embedding retention analytics for a specific coach.
   * Note: Access is restricted - COACH can only access their own data, ADMIN/OWNER can access any coach.
   */
  async getCoachAnalyticsRatings(coachId: number) {
    return this.client.get<MetabaseEmbedOut>(CoachEndpoints.BASE_URL, `/${coachId}/analytics/ratings`)
  }

  /**
   * Retrieves url for embedding retention analytics for a specific coach.
   * Note: Access is restricted - COACH can only access their own data, ADMIN/OWNER can access any coach.
   */
  async getCoachAnalyticsRetention(coachId: number, startDateRange: Date[]) {
    return this.client.get<MetabaseEmbedOut>(CoachEndpoints.BASE_URL, `/${coachId}/analytics/retention`, {
      startDateRange
    })
  }

  /**
   * Retrieves url for embedding retention analytics for all coaches.
   * Note: Access is restricted - Only ADMIN/OWNER can access this endpoint.
   */
  async getAllCoachAnalyticsRatings(coachName?: string) {
    return this.client.get<MetabaseEmbedOut>(CoachEndpoints.BASE_URL, "/analytics/ratings", {
      ...(coachName && { coachName })
    })
  }

  /**
   * Retrieves url for embedding retention analytics for all coaches.
   * Note: Access is restricted - Only ADMIN/OWNER can access this endpoint.
   */
  async getAllCoachAnalyticsRetention(startDateRange: Date[], coachName?: string) {
    return this.client.get<MetabaseEmbedOut>(CoachEndpoints.BASE_URL, "/analytics/retention", {
      startDateRange,
      ...(coachName && { coachName })
    })
  }

  /**
   * Updates a coach's hourly rate.
   */
  async patchHourlyRate(coachId: number, payload: CoachPatchHourlyRateIn) {
    return this.client.patch<GenericOut>(CoachEndpoints.BASE_URL, `/${coachId}/hourly-rate`, payload)
  }

  /**
   * Updates a coach's isExecutiveCoach flag.
   */
  async patchIsExecutive(coachId: number, payload: CoachPatchIsExecutiveIn) {
    return this.client.patch<GenericOut>(CoachEndpoints.BASE_URL, `/${coachId}/is-executive`, payload)
  }

  /**
   * Updates a coach's payee-name.
   */
  async patchPayeeName(coachId: number, payload: CoachPatchPayeeNameIn) {
    return this.client.patch<GenericOut>(CoachEndpoints.BASE_URL, `/${coachId}/payee-name`, payload)
  }

  /**
   * Updates the currently authenticated coach's accepting clients value.
   */
  async patchAcceptingClients(payload: CoachPatchAcceptingClientsIn) {
    return this.client.patch<GenericOut>(CoachEndpoints.BASE_URL, "/me/accepting-clients", payload)
  }

  /**
   * Overwrites the user's current assigned coach, and assigns new specified one. Used by admins and owners in fe-dashboard
   */
  async patchAssigned(payload: CoachPostAssignedIn) {
    return this.client.patch<CoachGetAssignedOut>(CoachEndpoints.BASE_URL, "/assigned", payload)
  }

  /**
   * Overwrites the user's current assigned coach, and assigns new specified one.
   * Used by users in fe-app and retool
   */
  async postAssigned(payload: CoachPostAssignedIn) {
    return this.client.post<CoachGetAssignedOut>(CoachEndpoints.BASE_URL, "/assigned", payload)
  }
}
